// src/components/Phaser/functions/object.ts

import { getContext, getEngine } from "./index";
import { ObjectConfig } from "../types/config";
import { getImagePath } from "../../../utils/format/valueFormat";

let objectIdCount = 1;
function getUniqueObjectID(): string {
  objectIdCount += 1;
  return String(objectIdCount);
}

const normKey = (s?: string) => String(s ?? "").trim().replace(/\s+/g, "_");

/**
 * Adds an obstacle by name into the scene.
 * Returns the new string ID, or "-1" if not found.
 */
export const addObject = (name: string, x: number, y: number): string => {
  const ctx = getContext();
  const obstacle = ctx.obstacles.find((o) => o.name === name);
  if (!obstacle) return "-1";

  const id = getUniqueObjectID();

  const newObj: ObjectConfig = {
    id,
    // clé texture partagée = nom normalisé (pas d’ID)
    idPhaser: normKey(obstacle.name),
    name,
    x,
    y,
    width: 50,
    height: 50,
    immovable: false,
    pickable: false,
    pickOrder: 0,
    pushable: false,
    assets: {
      // URL complète/valide pour le loader
      texture: getImagePath(obstacle.img),
    },
  };

  ctx.addObjectCtx(newObj);
  return newObj.id;
};

/**
 * Ajoute un objet en précisant largeur et hauteur.
 * Retourne l'id (string) ou "-1" si échec.
 */
export const addObjectWithSize = (
  name: string,
  x: number,
  y: number,
  width: number,
  height: number
): string => {
  const ctx = getContext();
  const obstacle = ctx.obstacles.find((o) => o.name === name);
  if (!obstacle) return "-1";

  const id = getUniqueObjectID();

  const newObj: ObjectConfig = {
    id,
    idPhaser: normKey(obstacle.name),
    name,
    x,
    y,
    width,
    height,
    immovable: false,
    pickable: false,
    pickOrder: 0,
    pushable: false,
    assets: { texture: getImagePath(obstacle.img) },
  };

  ctx.addObjectCtx(newObj);
  return newObj.id;
};

export const addPickable = (name: string, x: number, y: number): string => {
  const ctx = getContext();
  const obstacle = ctx.obstacles.find((o) => o.name === name);
  if (!obstacle) return "-1";

  const id = getUniqueObjectID();

  const newObj: ObjectConfig = {
    id,
    idPhaser: normKey(obstacle.name),
    name,
    x,
    y,
    width: 50,
    height: 50,
    immovable: false,
    pickable: true,
    pickOrder: 0,
    pushable: false,
    assets: {
      texture: getImagePath(obstacle.img),
    },
  };

  ctx.addObjectCtx(newObj);
  return newObj.id;
};

export const addPickableWithSize = (
  name: string,
  x: number,
  y: number,
  width: number,
  height: number
): string => {
  const ctx = getContext();
  const obstacle = ctx.obstacles.find((o) => o.name === name);
  if (!obstacle) return "-1";

  const id = getUniqueObjectID();

  const newObj: ObjectConfig = {
    id,
    idPhaser: normKey(obstacle.name),
    name,
    x,
    y,
    width,
    height,
    immovable: false,
    pickable: true,
    pickOrder: 0,
    pushable: false,
    assets: { texture: getImagePath(obstacle.img) },
  };

  ctx.addObjectCtx(newObj);
  return newObj.id;
};

export const addImmovable = (name: string, x: number, y: number): string => {
  const ctx = getContext();
  const obstacle = ctx.obstacles.find((o) => o.name === name);
  if (!obstacle) return "-1";

  const id = getUniqueObjectID();

  const newObj: ObjectConfig = {
    id,
    idPhaser: normKey(obstacle.name),
    name,
    x,
    y,
    width: 50,
    height: 50,
    immovable: true,
    pickable: false,
    pickOrder: 0,
    pushable: false,
    assets: {
      texture: getImagePath(obstacle.img),
    },
  };

  ctx.addObjectCtx(newObj);
  return newObj.id;
};

export const addImmovableWithSize = (
  name: string,
  x: number,
  y: number,
  width: number,
  height: number
): string => {
  const ctx = getContext();
  const obstacle = ctx.obstacles.find((o) => o.name === name);
  if (!obstacle) return "-1";

  const id = getUniqueObjectID();

  const newObj: ObjectConfig = {
    id,
    idPhaser: normKey(obstacle.name),
    name,
    x,
    y,
    width,
    height,
    immovable: true,
    pickable: false,
    pickOrder: 0,
    pushable: false,
    assets: { texture: getImagePath(obstacle.img) },
  };

  ctx.addObjectCtx(newObj);
  return newObj.id;
};

export const addPushable = (name: string, x: number, y: number): string => {
  const ctx = getContext();
  const obstacle = ctx.obstacles.find((o) => o.name === name);
  if (!obstacle) return "-1";

  const id = getUniqueObjectID();

  const newObj: ObjectConfig = {
    id,
    idPhaser: normKey(obstacle.name),
    name,
    x,
    y,
    width: 50,
    height: 50,
    immovable: false,
    pickable: false,
    pickOrder: 0,
    pushable: true,
    assets: {
      texture: getImagePath(obstacle.img),
    },
  };

  ctx.addObjectCtx(newObj);
  return newObj.id;
};

export const addPushableWithSize = (
  name: string,
  x: number,
  y: number,
  width: number,
  height: number
): string => {
  const ctx = getContext();
  const obstacle = ctx.obstacles.find((o) => o.name === name);
  if (!obstacle) return "-1";

  const id = getUniqueObjectID();

  const newObj: ObjectConfig = {
    id,
    idPhaser: normKey(obstacle.name),
    name,
    x,
    y,
    width,
    height,
    immovable: false,
    pickable: false,
    pickOrder: 0,
    pushable: true,
    assets: { texture: getImagePath(obstacle.img) },
  };

  ctx.addObjectCtx(newObj);
  return newObj.id;
};

export const addDecor = (name: string, x: number, y: number): string => {
  const ctx = getContext();
  const obstacle = ctx.obstacles.find((o) => o.name === name);
  if (!obstacle) return "-1";

  const id = getUniqueObjectID();

  const newObj: ObjectConfig = {
    id,
    idPhaser: normKey(obstacle.name),
    name,
    x,
    y,
    width: 50,
    height: 50,
    immovable: false,
    pickable: false,
    pickOrder: 0,
    pushable: false,
    decor: true,
    assets: {
      texture: getImagePath(obstacle.img),
    },
  };

  ctx.addObjectCtx(newObj);
  return newObj.id;
};

export const addDecorWithSize = (
  name: string,
  x: number,
  y: number,
  width: number,
  height: number
): string => {
  const ctx = getContext();
  const obstacle = ctx.obstacles.find((o) => o.name === name);
  if (!obstacle) return "-1";

  const id = getUniqueObjectID();

  const newObj: ObjectConfig = {
    id,
    idPhaser: normKey(obstacle.name),
    name,
    x,
    y,
    width,
    height,
    immovable: false,
    pickable: false,
    pickOrder: 0,
    pushable: false,
    decor: true,
    assets: { texture: getImagePath(obstacle.img) },
  };

  ctx.addObjectCtx(newObj);
  return newObj.id;
};

/**
 * Moves an existing object (same one created by addObject).
 */
export function moveObject(id: string | number, x: number, y: number): boolean {
  const ctx = getContext();
  ctx.updateObjectCtx(String(id), { x, y });
  return true;
}

/**
 * Removes (destroys) the object you created with addObject.
 */
export function destroyObject(id: string | number): boolean {
  const ctx = getContext();
  ctx.removeObjectCtx(String(id));
  return true;
}

/**
 * Globally expose a helper to make an object pickable at runtime.
 */
export function activatePickOrder(id: string | number, pickOrder: number): boolean {
  console.log("[OBJECT] activatePickOrder called with ID:", id, "pickOrder:", pickOrder);
  const engine = getEngine();
  console.log("[OBJECT] Engine available:", !!engine);
  const scene = engine?.phaserScene;
  console.log("[OBJECT] Scene available:", !!scene);
  if (!scene) {
    console.warn("[OBJECT] No scene available for activatePickOrder");
    return false;
  }

  const idStr = String(id);
  console.log("[OBJECT] Attempting to activate pick order for object:", idStr);

  try {
    scene.clearObjectImmovable(idStr);
    scene.clearObjectPushable(idStr);
    scene.clearObjectDecor(idStr);
    scene.setObjectPickable(idStr, pickOrder);
    console.log("[OBJECT] Successfully activated pick order for object:", idStr);
    return true;
  } catch (error) {
    console.error("[OBJECT] Error activating pick order for object:", idStr, error);
    return false;
  }
}

/**
 * Rend un objet pickable immédiatement, sans notion d’ordre.
 */
export async function activatePickable(id: string | number): Promise<boolean> {
  console.log("[OBJECT] activatePickable called with ID:", id);
  const engine = getEngine();
  console.log("[OBJECT] Engine available:", !!engine);
  const scene = engine?.phaserScene;
  console.log("[OBJECT] Scene available:", !!scene);
  if (!scene) {
    console.warn("[OBJECT] No scene available for activatePickable");
    return false;
  }

  const idStr = String(id);
  console.log("[OBJECT] Attempting to activate pickable for object:", idStr);

  // Check if object exists in the scene
  const obj = scene.getObjectById(idStr);
  console.log("[OBJECT] Object found in scene:", !!obj);

  // Check if object exists in engine
  const engineObj = engine.getObject(idStr);
  console.log("[OBJECT] Object found in engine:", !!engineObj);

  // List all objects in scene
  const allSceneObjects = scene.getAllObjectIds();
  console.log("[OBJECT] All scene objects:", allSceneObjects);

  // List all objects in engine
  const allEngineObjects = engine.getAllObjectIds();
  console.log("[OBJECT] All engine objects:", allEngineObjects);

  if (obj) {
    console.log("[OBJECT] Object details:", {
      id: obj.id,
      pickable: obj.pickable,
      immovable: obj.immovable,
      pushable: obj.pushable,
      decor: obj.decor,
      sprite: !!obj.sprite
    });
  }

  if (engineObj) {
    console.log("[OBJECT] Engine object details:", {
      id: engineObj.id,
      pickable: engineObj.pickable,
      immovable: engineObj.immovable,
      pushable: engineObj.pushable,
      decor: engineObj.decor,
      sprite: !!engineObj.sprite
    });
  }

  try {
    // Force object synchronization before activation
    console.log("[OBJECT] Forcing object synchronization...");
    const allObjects = engine.getAllObjectIds();
    console.log("[OBJECT] Objects before sync:", allObjects);

    // If object not found, try to sync it
    if (!obj && !engineObj) {
      console.log("[OBJECT] Object not found, attempting to sync from context...");
      // Try to get object from context
      const context = getContext();
      const contextObject = context.obstacles.find(o => String(o.id) === idStr);
      if (contextObject) {
        console.log("[OBJECT] Found object in context, but TObstacle structure doesn't match ObjectConfig");
        console.log("[OBJECT] TObstacle properties:", Object.keys(contextObject));
        console.log("[OBJECT] Attempting to force scene sync...");

        // Try to force sync by calling the scene's handleObjects method
        if (scene.handleObjects) {
          console.log("[OBJECT] Calling scene.handleObjects to force sync...");
          // Don't pass obstacles directly due to type mismatch
          scene.handleObjects([]);
          // Wait for sync to complete
          await new Promise(resolve => setTimeout(resolve, 100));

          // Check if object is now available
          const syncedObj = scene.getObjectById(idStr);
          const syncedEngineObj = engine.getObject(idStr);
          console.log("[OBJECT] Object in scene after force sync:", !!syncedObj);
          console.log("[OBJECT] Object in engine after force sync:", !!syncedEngineObj);
        } else {
          console.log("[OBJECT] scene.handleObjects not available");
        }
      }
    }

    // Wait for object to be available if not found initially
    if (!obj && !engineObj) {
      console.log("[OBJECT] Object still not found, waiting for sync...");
      let attempts = 0;
      const maxAttempts = 10;

      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        const waitingObj = scene.getObjectById(idStr);
        const waitingEngineObj = engine.getObject(idStr);

        if (waitingObj || waitingEngineObj) {
          console.log(`[OBJECT] Object found after ${attempts + 1} attempts`);
          break;
        }
        attempts++;
      }

      if (attempts >= maxAttempts) {
        console.log("[OBJECT] Object not found after waiting, proceeding anyway...");
      }
    }

    scene.clearObjectImmovable(idStr);
    scene.clearObjectPushable(idStr);
    scene.clearObjectDecor(idStr);
    scene.setObjectPickable(idStr);
    console.log("[OBJECT] Successfully activated pickable for object:", idStr);

    // Verify the object is now pickable
    const updatedObj = scene.getObjectById(idStr);
    const updatedEngineObj = engine.getObject(idStr);
    console.log("[OBJECT] Object in scene after activation:", !!updatedObj);
    console.log("[OBJECT] Object in engine after activation:", !!updatedEngineObj);

    // If still not found, try one more sync attempt
    if (!updatedObj && !updatedEngineObj) {
      console.log("[OBJECT] Object still not found after sync, retrying...");
      const context = getContext();
      const contextObject = context.obstacles.find(o => String(o.id) === idStr);
      if (contextObject) {
        console.log("[OBJECT] Re-syncing object from context...");
        console.log("[OBJECT] TObstacle structure mismatch - cannot sync directly");
        console.log("[OBJECT] Object may need to be re-added to the scene");
      }
    }

    if (updatedObj) {
      console.log("[OBJECT] Scene object after activation:", {
        pickable: updatedObj.pickable,
        immovable: updatedObj.immovable,
        pushable: updatedObj.pushable
      });
    }

    if (updatedEngineObj) {
      console.log("[OBJECT] Engine object after activation:", {
        pickable: updatedEngineObj.pickable,
        immovable: updatedEngineObj.immovable,
        pushable: updatedEngineObj.pushable
      });
    }

    // Check collision system status
    console.log("[OBJECT] Collision system available:", true);

    return true;
  } catch (error) {
    console.error("[OBJECT] Error activating pickable for object:", idStr, error);
    return false;
  }
}

/**
 * Resize an existing object at runtime.
 */
export function resizeObject(
  id: string | number,
  width: number,
  height: number
): boolean {
  const engine = getEngine();
  const scene = engine?.phaserScene;
  if (!scene) return false;

  const obj = scene.getObjectById(String(id));
  if (!obj || !obj.sprite) return false;

  obj.width = width;
  obj.height = height;
  obj.sprite.setDisplaySize(width, height);

  return true;
}

/**
 * Make an object immovable (cannot be pushed) at runtime.
 */
export async function activateImmovable(id: string | number): Promise<boolean> {
  console.log("[OBJECT] activateImmovable called with ID:", id);
  const engine = getEngine();
  const scene = engine?.phaserScene;
  if (!scene) return false;

  const idStr = String(id);

  // Check if object exists in the scene
  const obj = scene.getObjectById(idStr);
  const engineObj = engine.getObject(idStr);

  // If object not found, try to sync it
  if (!obj && !engineObj) {
    console.log("[OBJECT] Object not found for activateImmovable, attempting sync...");
    const context = getContext();
    const contextObject = context.obstacles.find(o => String(o.id) === idStr);
    if (contextObject) {
      console.log("[OBJECT] Found object in context for activateImmovable");
      // Try to force sync
      if (scene.handleObjects) {
        scene.handleObjects([]);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // Wait for object to be available
    let attempts = 0;
    const maxAttempts = 10;
    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      const waitingObj = scene.getObjectById(idStr);
      const waitingEngineObj = engine.getObject(idStr);
      if (waitingObj || waitingEngineObj) {
        console.log(`[OBJECT] Object found for activateImmovable after ${attempts + 1} attempts`);
        break;
      }
      attempts++;
    }
  }

  scene.clearObjectPickable(idStr);
  scene.clearObjectPushable(idStr);
  scene.clearObjectDecor(idStr);
  scene.setObjectImmovable(idStr);
  console.log("[OBJECT] Successfully activated immovable for object:", idStr);
  return true;
}

/**
 * Make an object pushable (can be shoved around) at runtime.
 */
export async function activatePushable(id: string | number): Promise<boolean> {
  console.log("[OBJECT] activatePushable called with ID:", id);
  const engine = getEngine();
  const scene = engine?.phaserScene;
  if (!scene) return false;

  const idStr = String(id);

  // Check if object exists in the scene
  const obj = scene.getObjectById(idStr);
  const engineObj = engine.getObject(idStr);

  // If object not found, try to sync it
  if (!obj && !engineObj) {
    console.log("[OBJECT] Object not found for activatePushable, attempting sync...");
    const context = getContext();
    const contextObject = context.obstacles.find(o => String(o.id) === idStr);
    if (contextObject) {
      console.log("[OBJECT] Found object in context for activatePushable");
      // Try to force sync
      if (scene.handleObjects) {
        scene.handleObjects([]);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // Wait for object to be available
    let attempts = 0;
    const maxAttempts = 10;
    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      const waitingObj = scene.getObjectById(idStr);
      const waitingEngineObj = engine.getObject(idStr);
      if (waitingObj || waitingEngineObj) {
        console.log(`[OBJECT] Object found for activatePushable after ${attempts + 1} attempts`);
        break;
      }
      attempts++;
    }
  }

  scene.clearObjectPickable(idStr);
  scene.clearObjectImmovable(idStr);
  scene.clearObjectDecor(idStr);
  scene.setObjectPushable(idStr);
  console.log("[OBJECT] Successfully activated pushable for object:", idStr);
  return true;
}

/**
 * Stop treating an object as pickable.
 */
export function desactivatePickOrder(id: string | number): boolean {
  const scene = getEngine()?.phaserScene;
  if (!scene) return false;
  scene.clearObjectPickable(String(id));
  return true;
}

/**
 * Stop treating an object as immovable.
 */
export function desactivateImmovable(id: string | number): boolean {
  const scene = getEngine()?.phaserScene;
  if (!scene) return false;
  scene.clearObjectImmovable(String(id));
  return true;
}

/**
 * Stop treating an object as pushable.
 */
export function desactivatePushable(id: string | number): boolean {
  const scene = getEngine()?.phaserScene;
  if (!scene) return false;
  scene.clearObjectPushable(String(id));
  return true;
}

/**
 * Marque un objet comme décor (immobile, sans gravité, non pickable/pushable).
 */
export function activateDecor(id: string | number): boolean {
  const engine = getEngine();
  const scene = engine?.phaserScene;
  if (!scene) return false;

  scene.clearObjectPickable(String(id));
  scene.clearObjectPushable(String(id));
  scene.clearObjectImmovable(String(id));
  scene.setObjectDecor(String(id));
  return true;
}

/**
 * Enlève le mode décor (réactive la gravité et la dynamique).
 */
export function desactivateDecor(id: string | number): boolean {
  const engine = getEngine();
  const scene = engine?.phaserScene;
  if (!scene) return false;
  scene.clearObjectDecor(String(id));
  return true;
}
